/**
 * Lightweight scale utilities — replaces Vega/D3 scale functions.
 */

/**
 * Linear scale: maps a numeric domain to a pixel range.
 */
export function linearScale(
  domain: [number, number],
  range: [number, number]
): (value: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1;
  return (value: number) => r0 + ((value - d0) / span) * (r1 - r0);
}

/**
 * Band scale: maps discrete categories to equal-width bands within a range.
 * Returns the start position of each band.
 */
export function bandScale(categories: string[], range: [number, number], padding = 0.1) {
  const [r0, r1] = range;
  const totalRange = r1 - r0;
  const n = categories.length || 1;
  const step = totalRange / (n + padding * 2);
  const bandwidth = step * (1 - padding);
  const offset = step * padding;

  const lookup = new Map<string, number>();
  categories.forEach((cat, i) => {
    lookup.set(cat, r0 + offset + i * step);
  });

  const scale = (category: string) => lookup.get(category) ?? r0;
  scale.bandwidth = () => bandwidth;
  scale.step = () => step;

  return scale;
}

/**
 * Compute nice axis ticks for a given domain.
 */
export function niceAxisTicks(min: number, max: number, targetCount = 5): number[] {
  if (min === max) return [min];

  const range = max - min;
  const roughStep = range / targetCount;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;

  let niceStep: number;
  if (residual <= 1.5) niceStep = magnitude;
  else if (residual <= 3) niceStep = 2 * magnitude;
  else if (residual <= 7) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;

  // Count data is always integers — don't show decimal ticks like 0.2, 0.4 …
  if (niceStep < 1) niceStep = 1;

  const niceMin = Math.floor(min / niceStep) * niceStep;
  const niceMax = Math.ceil(max / niceStep) * niceStep;

  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + niceStep * 0.5; v += niceStep) {
    ticks.push(Math.round(v * 1e10) / 1e10); // avoid floating-point noise
  }
  return ticks;
}
